import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLES = {
  paid: "border-volt/40 bg-volt/10 text-volt",
  created: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
  failed: "border-red-500/40 bg-red-500/10 text-red-400",
};

const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments")
      .then((r) => setPayments(r.data))
      .catch(() => toast.error("Failed to load payments"))
      .finally(() => setLoading(false));
  }, []);

  const totalPaid = payments.filter((p) => p.status === "paid").reduce((s, p) => s + p.amount, 0);
  const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="mt-6" data-testid="payments-tab">
      <div className="mb-5 flex items-center gap-3">
        <span className="rounded-full border border-volt/40 bg-volt/10 px-4 py-1.5 font-display text-sm font-bold uppercase tracking-widest text-volt" data-testid="payments-total">
          Collected: ₹{totalPaid.toLocaleString("en-IN")}
        </span>
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">{payments.length} transactions</span>
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-zinc-800 md:block" data-testid="payments-table-wrapper">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
        ) : payments.length === 0 ? (
          <p className="py-16 text-center text-sm text-zinc-500" data-testid="payments-empty">No payments yet.</p>
        ) : (
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="payments-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-surface font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Type</th>
                <th className="px-5 py-4">Detail</th>
                <th className="px-5 py-4">Amount</th>
                <th className="px-5 py-4">Payment ID</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-800/60 hover:bg-surface/60" data-testid={`payment-row-${p.id}`}>
                  <td className="px-5 py-4 font-semibold text-white">{p.name || "—"}</td>
                  <td className="px-5 py-4 capitalize text-zinc-400">{p.type}</td>
                  <td className="max-w-[220px] truncate px-5 py-4 text-zinc-300">{p.detail}</td>
                  <td className="px-5 py-4 font-display font-bold text-white">₹{p.amount.toLocaleString("en-IN")}</td>
                  <td className="px-5 py-4 font-mono text-xs text-zinc-500">{p.payment_id || "—"}</td>
                  <td className="px-5 py-4 text-xs text-zinc-500">{fmt(p.created_at)}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full border px-3 py-1 font-display text-xs font-bold uppercase tracking-widest ${STATUS_STYLES[p.status] || STATUS_STYLES.created}`} data-testid={`payment-status-${p.id}`}>
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="space-y-3 md:hidden" data-testid="payments-cards">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
        ) : payments.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 py-16 text-center text-sm text-zinc-500">No payments yet.</p>
        ) : (
          payments.map((p) => (
            <div key={p.id} className="rounded-xl border border-zinc-800 bg-surface p-4" data-testid={`payment-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{p.name || p.detail}</p>
                  <p className="text-xs capitalize text-zinc-500">{p.type}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-3 py-1 font-display text-xs font-bold uppercase tracking-widest ${STATUS_STYLES[p.status] || STATUS_STYLES.created}`}>
                  {p.status}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-400">
                <span className="font-display text-lg font-extrabold text-volt">₹{p.amount.toLocaleString("en-IN")}</span>
                <span>{fmt(p.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PaymentsTab;
